/**
 * Lightweight per-scene audio helper.
 *
 * Wraps HTMLAudioElement playback with two ergonomics every game
 * scene needs:
 *
 *   1. Looping background music that survives the browser autoplay
 *      block — if the first `play()` is rejected (no user gesture
 *      yet), a one-shot pointer/key/touch listener retries on the
 *      very first interaction and then detaches itself.
 *   2. Fire-and-forget one-shot SFX (coin pickups, etc.) that can
 *      re-trigger from the start back-to-back without spawning a
 *      new element each time.
 *
 * Every method is best-effort: any error is swallowed so missing
 * files or a locked AudioContext never break gameplay. Call
 * {@link destroy} from the owning scene's `destroy()` to stop all
 * playback and release the elements + any pending gesture listener.
 */

// Shared "you finished the whole game" victory jingle, played on the
// winner screen across every game. It lives under the simulator asset
// folder (the first game to ship it); the path is intentionally shared
// so frogger, snake, the simulator, etc. all celebrate with the exact
// same sound.
const GAME_FINISHED_URL = 'assets/simulator/sounds/game-finished.mp3';

export class SceneAudio {
    /** @type {HTMLAudioElement|null} */
    _loopEl = null;
    /** @type {Map<string, HTMLAudioElement>} */
    _oneShots = new Map();
    /** @type {(() => void)|null} */
    _gestureHandler = null;

    /**
     * Start (or restart) the single looping background track.
     * Idempotent for the same url — calling again while that track
     * is already loaded just ensures it's playing.
     *
     * @param {string} url
     * @param {number} [volume=0.32] 0..1
     */
    playLoop(url, volume = 0.32) {
        try {
            if (!this._loopEl || this._loopEl.dataset?.src !== url) {
                this._stopLoop();
                const el = new Audio(url);
                el.loop = true;
                el.preload = 'auto';
                el.volume = volume;
                // Stash the source so the idempotency check above can
                // tell whether we're being asked for the same track.
                try { el.dataset.src = url; } catch (_) {}
                // Belt-and-suspenders looping: some browsers/codecs don't
                // honour the `loop` flag reliably on a dynamically created
                // <audio> (a stray `ended` slips through and playback
                // stops). Restart from the top whenever that happens so
                // the track truly repeats indefinitely.
                el.addEventListener('ended', () => {
                    try { el.currentTime = 0; el.play()?.catch?.(() => {}); } catch (_) {}
                });
                this._loopEl = el;
            } else {
                this._loopEl.volume = volume;
            }
            const p = this._loopEl.play();
            if (p && typeof p.catch === 'function') {
                p.catch(() => this._armGesture());
            }
        } catch (_) {
            this._armGesture();
        }
    }

    /**
     * Play a short sound effect from the start. The element is
     * cached per-url so repeated calls don't leak audio nodes;
     * resetting `currentTime` lets it re-fire even mid-playback.
     *
     * @param {string} url
     * @param {number} [volume=0.9] 0..1
     */
    playOneShot(url, volume = 0.9) {
        try {
            let el = this._oneShots.get(url);
            if (!el) {
                el = new Audio(url);
                el.preload = 'auto';
                this._oneShots.set(url, el);
            }
            el.volume = volume;
            el.currentTime = 0;
            const p = el.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch (_) { /* silent */ }
    }

    /**
     * Play the shared one-shot victory jingle shown on the winner
     * screen when a game is won. The looping background track is
     * stopped first so the song plays clean over a quiet bed (the
     * game is over, so the loop stays down until the scene unmounts).
     * Best-effort like every other method here.
     *
     * @param {number} [volume=0.9] 0..1
     */
    playFinished(volume = 0.9) {
        this.stopLoop();
        this.playOneShot(GAME_FINISHED_URL, volume);
    }

    /**
     * Arm a one-shot gesture listener that retries the looping
     * track on the first user interaction (autoplay-policy
     * workaround). Detaches itself after firing once.
     */
    _armGesture() {
        if (typeof window === 'undefined' || this._gestureHandler) return;
        const handler = () => {
            this._disarmGesture();
            try { this._loopEl?.play?.().catch(() => {}); } catch (_) {}
        };
        this._gestureHandler = handler;
        window.addEventListener('pointerdown', handler, { once: true });
        window.addEventListener('keydown', handler, { once: true });
        window.addEventListener('touchstart', handler, { once: true });
    }

    _disarmGesture() {
        if (typeof window === 'undefined' || !this._gestureHandler) return;
        const handler = this._gestureHandler;
        window.removeEventListener('pointerdown', handler);
        window.removeEventListener('keydown', handler);
        window.removeEventListener('touchstart', handler);
        this._gestureHandler = null;
    }

    /**
     * Stop the looping background track (public). Safe to call when
     * nothing is playing. After this a later {@link playLoop} spins
     * up a fresh element and starts the track from the beginning.
     */
    stopLoop() {
        this._disarmGesture();
        this._stopLoop();
    }

    _stopLoop() {
        if (this._loopEl) {
            try { this._loopEl.pause(); } catch (_) {}
            this._loopEl = null;
        }
    }

    /** Stop everything + release elements and listeners. */
    destroy() {
        this._disarmGesture();
        this._stopLoop();
        for (const el of this._oneShots.values()) {
            try { el.pause(); } catch (_) {}
        }
        this._oneShots.clear();
    }
}
